import * as Sentry from "@sentry/vue"
import type { App } from "vue"
import type { Router } from "vue-router"
import { extractCentralFromUrl } from "@/analytics/utils"
import { getEnvironment } from "./utils"
import { ApiError } from "@/utils/ApiError"

// Public client-side identifier — ships in every bundle; deliberately in source, not .env.
// Empty until the itf-pakketransport project exists in Sentry (org itf-kb); initSentry is a no-op meanwhile.
const SENTRY_DSN = ""

let initialized = false

export interface SentryInitOptions {
  app: App
  router: Router
}

export function initSentry(options: SentryInitOptions): void {
  if (initialized) return

  if (!SENTRY_DSN) {
    console.warn("Sentry DSN not configured. Error tracking disabled.")
    return
  }

  Sentry.init({
    app: options.app,
    dsn: SENTRY_DSN,
    environment: getEnvironment(),
    integrations: [Sentry.browserTracingIntegration({ router: options.router })],
    tracesSampleRate: 0,
    sendDefaultPii: false,
    // Grows as real noise shows up in Sentry — comment every entry with its cause
    ignoreErrors: [],
    initialScope: {
      tags: { central: extractCentralFromUrl() }
    }
  })

  initialized = true
}

export function isSentryEnabled(): boolean {
  return initialized && !!SENTRY_DSN
}

export interface SentryUserContext {
  username: string
  companyId?: string
  companyName?: string
  taxiteamCode?: string
  taxiteamName?: string
}

export function setSentryUser(user: SentryUserContext): void {
  if (!isSentryEnabled()) return

  Sentry.setUser({ username: user.username })

  // central is tagged from the URL at init — don't overwrite it here
  Sentry.setTags({
    company_id: user.companyId || "unknown",
    company_name: user.companyName || "unknown",
    taxiteam_code: user.taxiteamCode || "unknown",
    taxiteam_name: user.taxiteamName || "unknown"
  })
}

export function clearSentryUser(): void {
  if (!isSentryEnabled()) return
  Sentry.setUser(null)
}

export interface CaptureOptions {
  tags?: Record<string, string>
  extra?: Record<string, unknown>
  // Overrides Sentry's default grouping so related events collapse into one issue
  fingerprint?: string[]
}

export function captureException(error: unknown, options: CaptureOptions = {}): void {
  if (!isSentryEnabled()) return

  const extra: Record<string, unknown> = { ...options.extra }
  if (error instanceof ApiError) {
    extra.status = error.status
    extra.responseBody = error.responseBody
  }

  Sentry.captureException(error, { extra, tags: options.tags, fingerprint: options.fingerprint })
}

export function captureWarning(message: string, options: CaptureOptions = {}): void {
  captureMessageWithLevel(message, "warning", options)
}

export function captureError(message: string, options: CaptureOptions = {}): void {
  captureMessageWithLevel(message, "error", options)
}

function captureMessageWithLevel(message: string, level: "warning" | "error", options: CaptureOptions): void {
  if (!isSentryEnabled()) return
  Sentry.captureMessage(message, { level, tags: options.tags, extra: options.extra, fingerprint: options.fingerprint })
}
